import { useState } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { BarChart3 } from 'lucide-react'
import { useProductPriceHistory } from '@/api/hooks'
import type { components } from '@/api/schema'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMoney } from '@/lib/utils'

type PriceHistoryDay = components['schemas']['PriceHistoryDayRead']
type CheckStatus = PriceHistoryDay['check_status']

const RANGES = [
  { value: '90', label: '3M' },
  { value: '365', label: '1Y' },
  { value: '800', label: 'All' },
] as const
type RangeDays = (typeof RANGES)[number]['value']

const CHECK_STATUS: Record<CheckStatus, { label: string; color: string }> = {
  observed: { label: 'Checked', color: '#525252' },
  unchanged_not_observed: { label: 'Not checked', color: '#e5e5e5' },
  not_in_feed: { label: 'Not in AQ feed', color: '#f59e0b' },
  check_failed: { label: 'Check failed', color: '#dc2626' },
}

const SERIES = [
  { key: 'store', label: 'Store Price', color: '#3b82f6', width: 2 },
  { key: 'net', label: 'Net Price', color: '#22c55e', width: 2 },
  { key: 'list', label: 'List Price', color: '#9ca3af', width: 1 },
  { key: 'map', label: 'MAP Price', color: '#f97316', width: 1 },
] as const

interface ChartPoint {
  date: string
  label: string
  status: CheckStatus
  changed: boolean
  store: number | null
  net: number | null
  list: number | null
  map: number | null
  tick: 1
}

function amount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

function fromDate(days: RangeDays): string {
  const start = new Date()
  start.setUTCDate(start.getUTCDate() - Number(days) + 1)
  return start.toISOString().slice(0, 10)
}

function dayLabel(isoDate: string, withYear: boolean): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  })
}

function toPoints(days: PriceHistoryDay[]): ChartPoint[] {
  // Start at the first day with any price; earlier days predate the product's history.
  const first = days.findIndex((day) => day.observation !== null || day.decision !== null)
  return days.slice(first === -1 ? days.length : first).map((day) => ({
    date: day.date,
    label: dayLabel(day.date, true),
    status: day.check_status,
    changed: day.observation_changed,
    store: amount(day.decision?.store_price),
    net: amount(day.decision?.net_price),
    list: amount(day.observation?.list_price),
    map: amount(day.decision?.map_price),
    tick: 1,
  }))
}

function HistoryTooltip({ active, payload }: TooltipProps<number, string>) {
  const point = payload?.[0]?.payload as ChartPoint | undefined
  if (!active || !point) return null
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      <p className="font-medium">{point.label}</p>
      <dl className="mt-1 grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 tabular-nums">
        {SERIES.map((series) =>
          point[series.key] === null ? null : (
            <div key={series.key} className="contents">
              <dt className="text-muted-foreground">{series.label}</dt>
              <dd className="text-right">{formatMoney(point[series.key])}</dd>
            </div>
          ),
        )}
      </dl>
      <p className="mt-1 text-muted-foreground">
        {CHECK_STATUS[point.status].label}
        {point.changed ? ' · AQ price changed' : ''}
      </p>
    </div>
  )
}

function statusRuns(points: ChartPoint[]): { status: CheckStatus; days: number; from: string }[] {
  const runs: { status: CheckStatus; days: number; from: string }[] = []
  for (const point of points) {
    const last = runs.at(-1)
    if (last && last.status === point.status) last.days += 1
    else runs.push({ status: point.status, days: 1, from: point.label })
  }
  return runs
}

function CheckStrip({ points }: { points: ChartPoint[] }) {
  const present = (Object.keys(CHECK_STATUS) as CheckStatus[]).filter((status) =>
    points.some((point) => point.status === status),
  )
  return (
    <div className="mt-1">
      <div className="flex h-2.5 gap-px overflow-hidden rounded-sm" style={{ marginLeft: 80, marginRight: 5 }}>
        {statusRuns(points).map((run) => (
          <div
            key={run.from}
            title={`${CHECK_STATUS[run.status].label} from ${run.from} (${run.days} ${run.days === 1 ? 'day' : 'days'})`}
            style={{ flexGrow: run.days, flexBasis: 0, backgroundColor: CHECK_STATUS[run.status].color }}
          />
        ))}
      </div>
      <ul
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
        style={{ marginLeft: 80 }}
        aria-label="AQ check status"
      >
        {present.map((status) => (
          <li key={status} className="flex items-center gap-1.5">
            <span
              className="inline-block size-2.5 rounded-sm border border-black/10"
              style={{ backgroundColor: CHECK_STATUS[status].color }}
            />
            {CHECK_STATUS[status].label}
          </li>
        ))}
      </ul>
    </div>
  )
}

function HistoryChart({ points }: { points: ChartPoint[] }) {
  const hasMap = points.some((point) => point.map !== null)
  const spansYears = points.length > 0 && points[0]?.date.slice(0, 4) !== points.at(-1)?.date.slice(0, 4)
  return (
    <div role="img" aria-label="Daily price history with AQ check status">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={points} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            fontSize={12}
            minTickGap={24}
            tickFormatter={(value: string) => dayLabel(value, spansYears)}
          />
          <YAxis tickFormatter={(v: number) => formatMoney(v)} fontSize={12} width={80} />
          <Tooltip content={<HistoryTooltip />} />
          <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 8 }} />
          {SERIES.filter((series) => series.key !== 'map' || hasMap).map((series) => (
            <Line
              key={series.key}
              type="stepAfter"
              dataKey={series.key}
              name={series.label}
              stroke={series.color}
              strokeWidth={series.width}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <CheckStrip points={points} />
    </div>
  )
}

export function ProductPriceHistoryCard({ productUuid }: { productUuid: string }) {
  const [range, setRange] = useState<RangeDays>('365')
  const history = useProductPriceHistory(productUuid, fromDate(range))
  const points = history.data ? toPoints(history.data.days) : []

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4" />
          Price History
        </CardTitle>
        <ReviewChoiceGroup label="Range" value={range} options={RANGES} onChange={setRange} />
      </CardHeader>
      <CardContent>
        {history.isPending ? (
          <Skeleton className="h-[300px] w-full" />
        ) : history.isError ? (
          <p className="text-sm text-muted-foreground">Price history could not load. Refresh to try again.</p>
        ) : points.length === 0 ? (
          <p className="text-sm text-muted-foreground">No price history in this range.</p>
        ) : (
          <HistoryChart points={points} />
        )}
      </CardContent>
    </Card>
  )
}
